/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { classes, VComponent } from '@adobe-commerce/elsie/lib';
import '@/quote-management/components/ItemsQuoted/ItemsQuoted.css';
import { Skeleton, SkeletonRow } from '@adobe-commerce/elsie/components';

export interface ItemsQuotedProps extends Omit<HTMLAttributes<HTMLDivElement>, 'loading'> {
  loading?: boolean;
  table?: VNode;
  pricesSummary?: VNode;
}

export const ItemsQuoted: FunctionComponent<ItemsQuotedProps> = ({
  className,
  loading,
  table,
  pricesSummary,
  ...props
}) => {
  if (loading) {
    return <ItemsQuotedSkeleton />;
  }

  return (
    <div className={classes(['quote-management-items-quoted', className])} {...props}>
      {table && (
        <VComponent
          node={table}
          className={classes(['quote-management-items-quoted__table'])}
          data-testid="quote-management-items-quoted__table"
        />
      )}
      {pricesSummary && (
        <VComponent
          node={pricesSummary}
          className={classes(['quote-management-items-quoted__prices-summary'])}
          data-testid="quote-management-items-quoted__prices-summary"
        />
      )}
    </div>
  );
};

export const ItemsQuotedSkeleton: FunctionComponent = () => {
  return (
    <Skeleton data-testid="items-quoted-skeleton">
      <SkeletonRow variant="row" fullWidth={true} size="medium" lines={4} multilineGap='xsmall' />
    </Skeleton>
  );
};